import React, { ReactNode, Component } from 'react';
import { BaseErrorBoundary, BaseErrorBoundaryProps } from './BaseErrorBoundary';
import { createNotification } from '../../utils/dom';
import { trackError, ErrorSeverity, type ErrorContext } from '../../utils/error-tracking';

interface ProviderErrorBoundaryProps extends BaseErrorBoundaryProps {
  children: ReactNode;
  providerName: string;
  onReset?: () => void;
  resetDependencies?: unknown[];
  fallbackProvider?: ReactNode;
  allowPartialFailure?: boolean;
  criticalError?: boolean;
  errorMessage?: string;
}

interface ProviderErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorCount: number;
  lastResetTime: number;
}

export class ProviderErrorBoundary extends Component<ProviderErrorBoundaryProps, ProviderErrorBoundaryState> {
  private resetAttempts = 0;
  private maxResetAttempts = 3;

  constructor(props: ProviderErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorCount: 0,
      lastResetTime: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ProviderErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { providerName, criticalError } = this.props;

    const trackingContext: ErrorContext = {
      component: providerName,
      boundary: 'provider',
      componentStack: errorInfo.componentStack,
      tags: {
        errorBoundary: 'provider',
        providerName,
        criticalError: String(criticalError),
        resetAttempts: String(this.resetAttempts),
        allowPartialFailure: String(!!this.props.allowPartialFailure),
      },
    };

    trackError(error, criticalError ? ErrorSeverity.CRITICAL : ErrorSeverity.ERROR, trackingContext);

    // Send notification about provider error
    const notificationType = criticalError ? 'error' : 'warning';
    const notificationMsg = criticalError
      ? `Critical error in ${providerName}. The application needs to restart.`
      : `Error loading ${providerName}. Attempting to recover...`;

    createNotification({
      type: notificationType,
      msg: notificationMsg,
      dismissed: false,
    });

    this.setState(prevState => ({
      errorCount: prevState.errorCount + 1,
    }));

    // Auto-reset for non-critical errors
    if (!criticalError && this.resetAttempts < this.maxResetAttempts) {
      setTimeout(() => {
        this.handleReset();
      }, 1000 * Math.pow(2, this.resetAttempts)); // Exponential backoff
    }
  }

  componentDidUpdate(prevProps: ProviderErrorBoundaryProps) {
    const { resetDependencies = [] } = this.props;

    // Check if any reset dependencies changed
    if (resetDependencies.length > 0 && prevProps.resetDependencies) {
      const hasChanged = resetDependencies.some(
        (dep, index) => dep !== prevProps.resetDependencies![index]
      );

      if (hasChanged && this.state.hasError) {
        this.handleReset();
      }
    }
  }

  handleReset = () => {
    const { onReset, providerName } = this.props;

    // Call custom reset handler if provided
    onReset?.();

    this.resetAttempts++;

    // Show reset notification
    createNotification({
      type: 'info',
      msg: `Resetting ${providerName}... (Attempt ${this.resetAttempts} of ${this.maxResetAttempts})`,
      dismissed: false,
    });

    // Reset error state
    this.setState({
      hasError: false,
      error: null,
      lastResetTime: Date.now(),
    });
  };

  renderProviderErrorFallback = () => {
    const {
      providerName,
      allowPartialFailure,
      criticalError = false,
    } = this.props;
    const { error, errorCount } = this.state;

    // render() already returned fallbackProvider when partial failure allows it.

    return (
      <div className={`
        flex flex-col items-center justify-center p-8
        ${criticalError ? 'min-h-screen bg-red-50' : 'min-h-[400px] bg-amber-50'}
        rounded-lg border ${criticalError ? 'border-red-200' : 'border-amber-200'}
      `}>
        <div className="text-center max-w-md">
          <div className="mb-4">
            <svg
              className={`mx-auto h-12 w-12 ${criticalError ? 'text-red-500' : 'text-amber-500'}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {criticalError ? 'Critical Application Error' : `${providerName} Error`}
          </h3>

          <p className="text-gray-600 mb-4">
            {criticalError
              ? 'A critical error has occurred. The application needs to restart.'
              : `There was a problem initializing ${providerName}. Some features may not work correctly.`}
          </p>

          {error && process.env.NODE_ENV === 'development' && (
            <details className="mb-4 text-left">
              <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                Error details
              </summary>
              <div className="mt-2 p-3 bg-gray-100 rounded text-xs text-gray-700 overflow-auto max-h-40">
                <pre className="whitespace-pre-wrap">{error.stack}</pre>
              </div>
            </details>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {this.resetAttempts < this.maxResetAttempts && (
              <button
                onClick={this.handleReset}
                className={`px-4 py-2 text-white rounded-lg transition-colors ${
                  criticalError
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-amber-500 hover:bg-amber-600'
                }`}
              >
                Reset {providerName} ({this.maxResetAttempts - this.resetAttempts} attempts left)
              </button>
            )}

            {criticalError && (
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Reload Application
              </button>
            )}

            {!criticalError && !allowPartialFailure && (
              <button
                onClick={() => window.location.href = '/'}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Go to Home
              </button>
            )}
          </div>

          {errorCount > 1 && (
            <div className="mt-4 text-xs text-gray-500">
              This error has occurred {errorCount} times
            </div>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { hasError } = this.state;
    const { children, fallbackProvider, allowPartialFailure, ...baseProps } = this.props;

    if (hasError) {
      // If we allow partial failure and have a fallback, use it
      if (allowPartialFailure && fallbackProvider) {
        return fallbackProvider;
      }

      return (
        <BaseErrorBoundary
          {...baseProps}
          fallback={this.renderProviderErrorFallback()}
          level="section"
        >
          {children}
        </BaseErrorBoundary>
      );
    }

    // Wrap children in base error boundary for additional protection
    return (
      <BaseErrorBoundary {...baseProps} level="section">
        {children}
      </BaseErrorBoundary>
    );
  }
}

// HOC to wrap any provider with error boundary
export function withProviderErrorBoundary<P extends object>(
  ProviderComponent: React.ComponentType<P>,
  providerName: string,
  options?: Partial<ProviderErrorBoundaryProps>
) {
  const WrappedProvider = React.forwardRef<unknown, P>((props, ref) => {
    const componentProps = { ...props, ref } as P & { ref: React.Ref<unknown> };
    return (
      <ProviderErrorBoundary providerName={providerName} {...options}>
        <ProviderComponent {...componentProps} />
      </ProviderErrorBoundary>
    );
  });
  WrappedProvider.displayName = `withProviderErrorBoundary(${providerName})`;
  return WrappedProvider;
}

// Wrapper component
export const ProviderBoundary: React.FC<ProviderErrorBoundaryProps> = (props) => {
  return <ProviderErrorBoundary {...props} />;
};

export default ProviderErrorBoundary;